<template>
  <div>
    <div v-if="errored" class="errored">Une erreur s'est produite 😥</div>

    <div v-else-if="loaded" id="left-right-projects" :class="'resume ' + person.lang">
      <div class="banner">
        <img v-if="person.picture" :src="person.picture" />
        <div>
          <h1>{{ person.name.first }} {{ person.name.last }}</h1>
          <div class="position">
            {{ person.position }}<span v-if="person.birth.year"> — {{ lang.born }} {{ person.birth.year }}</span>
          </div>
        </div>
      </div>

      <div class="grid">
        <div v-if="person.experience.length" class="section experience">
          <h2>{{ lang.experience }}</h2>
          <div v-for="experience in person.experience" class="item">
            <h3>
              {{ experience.position }}, {{ experience.company }}<br />
              <span class="timeperiod">{{ experience.timeperiod_from }} - {{ experience.timeperiod_to || lang.today }}</span>
            </h3>
            <div v-if="experience.description" class="description">{{ experience.description }}</div>
          </div>
        </div>

        <div v-if="person.education.length" class="section education">
          <h2>{{ lang.education }}</h2>
          <div v-for="education in person.education" class="item">
            <h3>
              {{ education.degree_role }}, {{ education.degree_structure }} <br />
              <span class="timeperiod">{{ education.timeperiod_from }} - {{ education.timeperiod_to || lang.today }}</span>
            </h3>
            <div v-if="education.description" class="description">{{ education.description }}</div>
          </div>
        </div>

        <div class="section contact">
          <h2>{{ lang.contact }}</h2>
          <ul>
            <li v-if="person.contact.street">
              <i class="fa fa-home"></i>
              {{ person.contact.street }}, {{ person.contact.city }}
            </li>
            <li>
              <i class="fa fa-envelope"></i>
              <a :href="contactLinks.email">{{ person.contact.email }}</a>
            </li>
            <li>
              <i class="fa fa-phone"></i>
              <a :href="'tel:' + person.contact.phone">{{ person.contact.phone }}</a>
            </li>
            <li v-if="person.contact.website">
              <i class="fa fa-globe"></i>
              <a :href="person.contact.website" target="_blank">{{ person.contact.website }}</a>
            </li>
            <li v-if="person.contact.github">
              <i class="fa fa-github"></i>
              <a :href="contactLinks.github" target="_blank">{{ person.contact.github }}</a>
            </li>
          </ul>
        </div>

        <div class="skills">
          <h2 class="headline">{{ lang.skills }}</h2>
          <ul>
            <li v-for="skill in person.skills">
              <span>{{ skill.name }}</span>
              <span class="skill-bar">
                <span :style="'width: ' + skill.level + '%'" class="level"></span>
              </span>
            </li>
          </ul>
        </div>
      </div>

      <div v-if="person.projects.length" class="projects">
        <h2>{{ lang.projects }}</h2>
        <div v-for="project in person.projects" class="item">
          <h3>
            {{ project.name }}, {{ project.url }}<br />
            <span class="timeperiod">{{ project.timeperiod_from }} - {{ project.timeperiod_to }}</span>
            <br />{{ project.platform }}
          </h3>
          <div v-if="project.description" class="description">{{ project.description }}</div>
        </div>
      </div>
    </div>
    <div v-else class="loading">Chargement en cours ⏰</div>
  </div>
</template>

<script>
import Vue from 'vue';
import { getVueOptions } from './options';

const name = 'left-right-projects';
export default Vue.component(name, getVueOptions(name));
</script>

<style lang="less" scoped>
@page left-right-projects {
  margin: 2cm 0;
}

@page left-right-projects:first {
  margin-top: 0;
}

@primary-color: #4b5563;
@secondary-color: #581c87;
@third-color: #d1d5db;
@normal: 11pt;
@big: 26pt;
@small: 10pt;

#left-right-projects {
  font-family: 'Brown', sans-serif;
  font-weight: 300;
  font-size: @normal;
  padding: 0 2cm;
  box-sizing: border-box;
  color: @primary-color;
  line-height: 17pt;
  page: left-right-projects;
  .ar& {
    direction: rtl;
    font-family: 'Noto Sans Arabic', 'Brown', sans-serif;
    font-weight: 400;
  }

  .banner {
    display: grid;
    grid-template-columns: 1fr 1fr;
    column-gap: 1cm;
    margin: 2cm 0;
    align-items: center;

    img {
      width: 128px;
      height: 128px;
      border-radius: 50%;
      justify-self: end;
    }

    h1 {
      text-transform: uppercase;
      font-size: @big;
      line-height: 1;
      color: @secondary-color;
      margin: 0;
      .ar& {
        font-weight: 700;
      }
    }

    .position {
      text-transform: uppercase;
      font-size: @small;
    }
  }

  .item:not(:last-child) {
    margin-bottom: 0.3cm;
  }

  h2 {
    text-transform: uppercase;
    color: @secondary-color;
    margin-top: 0;
    margin-bottom: 0.5cm;
    .ar& {
      font-weight: 700;
    }
  }

  .grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 2cm 1cm;
  }

  .section {
    page-break-inside: avoid;
    break-inside: avoid;

    &:nth-child(odd) {
      direction: rtl;
      .ar& {
        direction: ltr;
      }
    }

    &:nth-child(even) {
      direction: ltr;
      .ar& {
        direction: rtl;
      }
    }
  }

  h3 {
    font-size: @normal;
    margin: 0;
    .ar& {
      font-weight: 700;
    }
  }

  .description {
    white-space: pre-line;
    text-align: justify;
  }

  .timeperiod {
    text-transform: capitalize;
  }

  .contact {
    direction: initial !important;
    text-align: end;
    .ar& {
      text-align: start;
    }

    ul {
      margin: 0;
      padding: 0;
      list-style: none;
      line-height: 1.6;
    }

    a {
      color: @primary-color;
      text-decoration: none;
    }
  }

  .skills ul {
    margin: 0;
    padding: 0;
    list-style: none;
    line-height: 1.6;

    li {
      display: grid;
      grid-template-columns: max-content 1fr;
      align-items: center;
      column-gap: 1cm;
    }

    .skill-bar {
      display: flex;
      background-color: @third-color;
      height: 5px;
      border-radius: 3px;
      overflow: hidden;
    }

    .level {
      background-color: @secondary-color;
      height: 100%;
    }
  }

  .projects {
    page-break-inside: avoid;
    break-inside: avoid;
    margin: 2cm 0;

    .description {
      text-align: initial;
    }
  }
}
</style>
